import type { ApiVehicle } from '@/services/vehicleApiService'
import type { GoMetroVehicle } from '@/services/goMetroVehicleApiService'
import type { AeversaVehicleListItem } from '@/services/aeversaVehicleApiService'
import type { UnifiedVehicle } from '@/types/vehicle'
import type { UnifiedDriver } from '@/types/driver'
import { regionCodeFromId, regionIdFromCode } from '@/services/regionScope'
import { resolveAssetType } from '@/utils/assetTypeUtils'
import { normalizeDriverCategory } from '@/config/driverCategory'

/** Map unified /api/vehicles response → legacy ApiVehicle shape for existing UI. */
export function unifiedVehicleToApiVehicle(v: UnifiedVehicle): ApiVehicle {
  const deviceId = v.device_id ?? v.telemetry_device?.deviceId ?? null
  const uber = v.uber_vehicle
  const ext = v as UnifiedVehicle & Record<string, unknown>
  const assetType = resolveAssetType({
    asset_type: v.asset_type,
    vehicle_type: v.vehicle_type ?? ext.vehicle_type,
    type: ext.type,
  })

  return {
    id: v.id,
    deviceId,
    name: v.telemetry_device?.name ?? null,
    year: v.year != null ? String(v.year) : null,
    model: v.model ?? '',
    number_plate: v.number_plate,
    colour: v.colour,
    status_on_gometro: v.telemetry_device?.status ?? null,
    status_on_amp_control: v.aeversa_id_tag ? 'linked' : null,
    status_on_valt_app: v.primary_status ?? null,
    status_on_uber: uber?.licensePlate ? 'linked' : null,
    initial_linked_driver: v.initial_linked_driver,
    licensePlate: uber?.licensePlate ?? v.number_plate,
    vin: v.vin ?? '',
    car_make: v.car_make ?? '',
    status: v.primary_status ?? v.sub_status ?? v.status_label ?? '',
    status_label: v.status_label ?? null,
    compliance_documents: v.compliance_documents ?? null,
    primary_status: v.primary_status,
    sub_status: v.sub_status,
    created_at: v.created_at ?? '',
    updated_at: v.updated_at ?? '',
    api_region: v.api_region ?? regionCodeFromId(v.region_id) ?? null,
    // Single-schema FK columns (embedded on vehicles row)
    region_id: v.region_id,
    device_id: deviceId,
    aeversa_id_tag: v.aeversa_id_tag,
    uber_vehicle_uuid: v.uber_vehicle_uuid,
    go_metro_vehicle_id: v.go_metro_vehicle_id,
    po_number: v.po_number ?? null,
    po_batch: v.po_batch ?? null,
    po_date: v.po_date ?? null,
    po_order_date: v.po_order_date ?? null,
    po_landed_date: v.po_landed_date ?? null,
    ...(assetType ? { asset_type: assetType, vehicle_type: assetType, type: assetType } : {}),
  } as ApiVehicle & Record<string, unknown>
}

/** Map legacy ApiVehicle → unified shape (for dual-read transition). */
export function apiVehicleToUnified(v: ApiVehicle & Record<string, unknown>): UnifiedVehicle {
  const regionId =
    typeof v.region_id === 'number'
      ? v.region_id
      : regionIdFromCode(typeof v.api_region === 'string' ? v.api_region : null)

  return {
    id: v.id,
    region_id: regionId,
    vin: v.vin,
    car_make: v.car_make,
    model: v.model,
    number_plate: v.number_plate,
    colour: v.colour,
    year: v.year,
    device_id: (v.device_id as string | null) ?? v.deviceId,
    uber_vehicle_uuid: (v.uber_vehicle_uuid as string | null) ?? null,
    aeversa_id_tag: (v.aeversa_id_tag as string | null) ?? null,
    go_metro_vehicle_id: (v.go_metro_vehicle_id as number | null) ?? null,
    initial_linked_driver: v.initial_linked_driver,
    primary_status: v.primary_status ?? null,
    sub_status: v.sub_status ?? null,
    created_at: v.created_at,
    updated_at: v.updated_at,
    telemetry_device: v.deviceId ? { id: 0, deviceId: v.deviceId, name: v.name, status: v.status_on_gometro } : null,
    uber_vehicle: v.licensePlate
      ? { licensePlate: v.licensePlate ?? undefined, make: v.car_make, model: v.model, year: v.year }
      : null,
    charging_tag: (v.aeversa_id_tag as string | null) ? { value: v.aeversa_id_tag as string } : null,
    compliance_documents: v.compliance_documents ?? null,
    api_region: v.api_region ?? null,
  }
}

/** Normalize any driver row from fragmented endpoints into UnifiedDriver. */
export function pickPositiveNumericId(...values: unknown[]): number | null {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
    if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
      const parsed = Number(value.trim())
      if (parsed > 0) return parsed
    }
  }
  return null
}

export function normalizeDriverRow(raw: Record<string, unknown>): UnifiedDriver {
  const regionId =
    typeof raw.region_id === 'number' ? raw.region_id : regionIdFromCode(raw.region as string | undefined)

  const firstName = String(raw.first_name ?? '')
  const lastName = String(raw.last_name ?? '')
  const id = pickPositiveNumericId(raw.id, raw.driver_id) ?? Number(raw.id)

  return {
    id,
    driver_id: String(raw.driver_id ?? raw.central_driver_id ?? raw.id ?? ''),
    region_id: regionId,
    org_id: (raw.org_id as string | null) ?? null,
    first_name: firstName,
    last_name: lastName,
    name: String(raw.name ?? `${firstName} ${lastName}`.trim()),
    phone_number: String(raw.phone_number ?? raw.phone ?? ''),
    email: (raw.email ?? raw.email_address) as string | null,
    driver_state: String(raw.driver_state ?? raw.status ?? 'applicant'),
    status: String(raw.status ?? raw.driver_state ?? 'applicant'),
    driver_type: (raw.driver_type as string | null) ?? null,
    team_ids: (raw.team_ids as string | null) ?? null,
    fcm_token: (raw.fcm_token as string | null) ?? null,
    device_id: (raw.device_id as string | null) ?? null,
    device_type: (raw.device_type as string | null) ?? null,
    image: (raw.image as string | null) ?? null,
    profile_picture: (raw.profile_picture as string | null) ?? null,
    id_number: (raw.id_number as string | null) ?? null,
    license_details: (raw.license_details as Record<string, unknown> | null) ?? null,
    id_passport_path: (raw.id_passport_path as string | null) ?? null,
    license_path: (raw.license_path as string | null) ?? null,
    bank_proof_path: (raw.bank_proof_path as string | null) ?? null,
    esquare_user_id: (raw.esquare_user_id as string | null) ?? null,
    vehicle_type: (() => {
      const value = (raw.vehicle_type as string | null) ?? (raw.type as string | null) ?? null
      return normalizeDriverCategory(value) ?? value
    })(),
    is_online: (raw.is_online as 0 | 1 | boolean | null) ?? null,
    platform_partner_driver:
      (raw.platform_partner_driver as string | null) ?? (raw.platform_partner_driver_id as string | null) ?? null,
    platform: (raw.platform as string | null) ?? null,
    date_onboarded: (raw.date_onboarded as string | null) ?? null,
    box_branding_incentive: (raw.box_branding_incentive as string | null) ?? null,
    offboard_date: (raw.offboard_date as string | null) ?? null,
    country: (raw.country as string | null) ?? null,
    created_at: (raw.created_at as string | null) ?? null,
    updated_at: (raw.updated_at as string | null) ?? null,
    region: raw.region as string | undefined,
    connection: raw.connection as string | undefined,
  }
}

/** Unified vehicle → legacy Go Metro list row (device_id is the telemetry FK; no separate go_metro_vehicles table). */
export function unifiedVehicleToGoMetroVehicle(v: UnifiedVehicle): GoMetroVehicle {
  const tel = (v.telemetry ?? {}) as Record<string, unknown>
  const deviceId = v.device_id ?? v.telemetry_device?.deviceId ?? ''

  return {
    id: v.go_metro_vehicle_id ?? v.telemetry_device?.id ?? v.id,
    deviceId,
    name: v.telemetry_device?.name ?? v.model ?? '',
    model: v.model ?? '',
    licensePlate: v.number_plate ?? '',
    vin: v.vin ?? '',
    status: v.telemetry_device?.status ?? v.primary_status ?? 'active',
    last_latitude: Number(tel.last_latitude ?? tel.latitude ?? 0),
    last_longitude: Number(tel.last_longitude ?? tel.longitude ?? 0),
    last_timestamp: Number(tel.last_timestamp ?? tel.timestamp ?? 0),
    last_speed: Number(tel.last_speed ?? tel.speed ?? 0),
    last_ignition_status: Boolean(tel.last_ignition_status ?? tel.ignition),
    last_movement_status: Boolean(tel.last_movement_status ?? tel.moving),
    last_altitude: Number(tel.last_altitude ?? 0),
    last_supply_voltage: Number(tel.last_supply_voltage ?? 0),
    last_can_data: (tel.last_can_data ?? tel.canData ?? null) as GoMetroVehicle['last_can_data'],
    created_at: v.created_at ?? '',
    updated_at: v.updated_at ?? '',
    valt_vehicle: {
      id: v.id,
      vin: v.vin ?? '',
      number_plate: v.number_plate ?? '',
      car_make: v.car_make ?? '',
      model: v.model ?? '',
    },
  }
}

/** Unified vehicle → legacy Aeversa list row (aeversa_id_tag column on vehicles; no aeversa_vehicles table). */
export function unifiedVehicleToAeversaListItem(v: UnifiedVehicle): AeversaVehicleListItem {
  return {
    aeversa_vehicle_id: v.id,
    aeversa_tag_id: v.aeversa_id_tag ?? '',
    aeversa_id_tag: v.aeversa_id_tag ?? '',
    synced_at: v.updated_at ?? v.created_at ?? '',
    valt_vehicle: {
      id: v.id,
      number_plate: v.number_plate ?? '',
      vin: v.vin ?? '',
      car_make: v.car_make ?? '',
      model: v.model ?? '',
      aeversa_id_tag: v.aeversa_id_tag ?? undefined,
      go_metro_vehicle_id: v.go_metro_vehicle_id ?? undefined,
      go_metro: v.telemetry_device
        ? {
            id: v.telemetry_device.id,
            deviceId: v.telemetry_device.deviceId ?? '',
            name: v.telemetry_device.name ?? '',
            licensePlate: v.number_plate ?? '',
          }
        : undefined,
    },
  }
}

export function vehicleHasTelemetry(v: UnifiedVehicle): boolean {
  return Boolean(v.device_id ?? v.telemetry_device?.deviceId)
}

export function vehicleHasChargingTag(v: UnifiedVehicle): boolean {
  return Boolean(v.aeversa_id_tag)
}

export function vehicleHasUberLink(v: UnifiedVehicle): boolean {
  return Boolean(v.uber_vehicle_uuid ?? v.uber_vehicle?.vehicleId)
}

/** Normalize unified driver → our-drivers page shape. */
export function unifiedDriverToOurDriver(d: UnifiedDriver): Record<string, unknown> {
  const ext = d as UnifiedDriver & Record<string, unknown>
  const partnerId = typeof d.platform_partner_driver === 'string' ? d.platform_partner_driver.trim() : ''
  return {
    id: d.id,
    driver_id: d.driver_id,
    region_id: d.region_id,
    org_id: d.org_id,
    first_name: d.first_name,
    last_name: d.last_name,
    name: d.name,
    phone_number: d.phone_number,
    email: d.email,
    driver_state: d.driver_state,
    status: d.status,
    license_number: d.license_details ? JSON.stringify(d.license_details) : undefined,
    id_number: d.id_number,
    vehicle_type: d.vehicle_type ?? null,
    is_online: d.is_online ?? 0,
    platform_partner_driver: partnerId || null,
    platform: (ext.platform as string | null | undefined) ?? d.platform ?? null,
    date_onboarded: (ext.date_onboarded as string | null | undefined) ?? d.date_onboarded ?? null,
    box_branding_incentive:
      (ext.box_branding_incentive as string | null | undefined) ?? d.box_branding_incentive ?? null,
    offboard_reason: (ext.offboard_reason as string | null | undefined) ?? null,
    offboard_date: (ext.offboard_date as string | null | undefined) ?? d.offboard_date ?? null,
    country: (ext.country as string | null | undefined) ?? d.country ?? null,
    created_at: d.created_at,
    updated_at: d.updated_at,
  }
}

/** Normalize unified driver → uber deployed drivers page shape. */
export function unifiedDriverToUberDeployed(d: UnifiedDriver): Record<string, unknown> {
  return {
    id: d.id,
    driver_id: d.driver_id,
    region_id: d.region_id,
    first_name: d.first_name,
    last_name: d.last_name,
    name: d.name,
    phone_number: d.phone_number,
    email: d.email,
    org_id: d.org_id,
    driver_state: d.driver_state,
    team_ids: d.team_ids,
    driver_type: d.driver_type,
    fcm_token: d.fcm_token,
    image: d.image,
    region: d.region ?? regionCodeFromId(d.region_id) ?? undefined,
    status: d.status ?? d.driver_state,
    created_at: d.created_at,
    updated_at: d.updated_at,
  }
}
